import {
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Spinner,
  Text,
  Textarea,
  VStack,
  useToast,
} from '@chakra-ui/react'
import FastAvatar from '../image/FastAvatar'
import { ExternalLink, MessageCircle, Minus, Send, X } from 'lucide-react'
import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  type HRConversation,
  type HREmployee,
  useHRConversations,
  useHRMessages,
  useMarkHRConversationRead,
  useSendHRMessage,
} from '../../api/hr-chat/hrChat'
import { useCurrentSelf } from '../../hooks/auth/useCurrentSelf'

const employeeName = (employee?: HREmployee | null) => {
  if (!employee) return 'Unassigned'
  return employee.full_name || `${employee.first_name || ''} ${employee.last_name || ''}`.trim() || employee.work_email || 'Employee'
}

const conversationTitle = (conversation: HRConversation, currentUserId?: string | null) => {
  if (conversation.requester.id === currentUserId) return 'My HR chat'
  return employeeName(conversation.requester)
}

const initials = (employee?: HREmployee | null) => (
  employeeName(employee).split(' ').map((part) => part[0]).join('').slice(0, 2).toUpperCase()
)

const HRChatWidget = () => {
  const toast = useToast()
  const navigate = useNavigate()
  const scrollRef = useRef<HTMLDivElement>(null)
  const lastMarkedReadRef = useRef('')
  const currentSelfQuery = useCurrentSelf()
  const currentUser = currentSelfQuery.data?.authUser
  const currentUserId = currentUser?.id ?? null

  const [isOpen, setIsOpen] = useState(false)
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null)
  const [draft, setDraft] = useState('')

  const conversationsQuery = useHRConversations(!!currentUser)
  const sendMessage = useSendHRMessage()
  const markRead = useMarkHRConversationRead()
  const conversations = useMemo(() => conversationsQuery.data ?? [], [conversationsQuery.data])
  const unreadConversations = useMemo(
    () => conversations.filter((conversation) => conversation.unread_count > 0),
    [conversations],
  )
  const unreadTotal = unreadConversations.reduce((sum, conversation) => sum + conversation.unread_count, 0)
  const visibleConversations = unreadConversations.length > 0 ? unreadConversations : conversations
  const selectedConversation = useMemo(
    () => visibleConversations.find((conversation) => conversation.id === selectedConversationId) ?? visibleConversations[0] ?? null,
    [selectedConversationId, visibleConversations],
  )
  const messagesQuery = useHRMessages(selectedConversation?.id ?? null, isOpen)
  const messages = messagesQuery.data ?? []
  const latestMessageId = messages[messages.length - 1]?.id ?? ''

  useEffect(() => {
    if (!isOpen) return
    setSelectedConversationId((current) => {
      if (current && visibleConversations.some((conversation) => conversation.id === current)) return current
      return visibleConversations[0]?.id ?? null
    })
  }, [isOpen, visibleConversations])

  useEffect(() => {
    if (!isOpen || !selectedConversation?.id || !latestMessageId) return
    const key = `${selectedConversation.id}:${latestMessageId}`
    if (lastMarkedReadRef.current === key) return
    lastMarkedReadRef.current = key
    markRead.mutate(selectedConversation.id)
  }, [isOpen, latestMessageId, markRead, selectedConversation?.id])

  useEffect(() => {
    if (!scrollRef.current) return
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight
  }, [messages.length, selectedConversation?.id])

  if (!currentUser || unreadTotal === 0) return null

  const sendDraft = () => {
    const body = draft.trim()
    if (!selectedConversation?.id || !body || sendMessage.isPending) return
    sendMessage.mutate(
      { conversationId: selectedConversation.id, body },
      {
        onSuccess: () => setDraft(''),
        onError: (error) => {
          toast({
            title: 'Message not sent',
            description: (error as Error).message,
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'bottom',
          })
        },
      },
    )
  }

  const handleSend = (event: FormEvent) => {
    event.preventDefault()
    sendDraft()
  }

  const openFullPage = () => {
    setIsOpen(false)
    navigate('/user/hr-chat')
  }

  if (!isOpen) {
    return (
      <Box position="fixed" right={{ base: '16px', md: '28px' }} bottom={{ base: '16px', md: '24px' }} zIndex={30}>
        <IconButton
          aria-label="Open unread HR chat"
          icon={<MessageCircle size={22} />}
          onClick={() => setIsOpen(true)}
          w="52px"
          h="52px"
          borderRadius="16px"
          bg="primary.100"
          color="white"
          boxShadow="0 12px 32px rgba(93,94,203,0.28)"
          _hover={{ bg: 'primary.200', transform: 'translateY(-1px)' }}
        />
        <Flex position="absolute" top="-6px" right="-6px" minW="20px" h="20px" px="6px" align="center" justify="center" borderRadius="999px" bg="error.100" color="white" fontSize="11px" fontWeight="700" border="2px solid white">
          {unreadTotal > 9 ? '9+' : unreadTotal}
        </Flex>
      </Box>
    )
  }

  return (
    <Flex
      position="fixed"
      right={{ base: '12px', md: '28px' }}
      bottom={{ base: '12px', md: '24px' }}
      zIndex={30}
      w={{ base: 'calc(100vw - 24px)', md: '380px' }}
      h={{ base: 'calc(100vh - 24px)', md: '520px' }}
      bg="rgba(255,255,255,0.96)"
      backdropFilter="blur(28px) saturate(180%)"
      borderRadius={{ base: '14px', md: '16px' }}
      boxShadow="0 22px 70px rgba(13,13,18,0.18)"
      border="1px solid"
      borderColor="rgba(0,0,0,0.06)"
      direction="column"
      overflow="hidden"
    >
      <Flex px="14px" py="12px" align="center" justify="space-between" borderBottom="1px solid" borderColor="rgba(0,0,0,0.06)">
        <HStack minW={0} spacing="9px">
          <FastAvatar size="sm" src={selectedConversation?.requester.profile_picture || undefined} name={initials(selectedConversation?.requester)} />
          <Box minW={0}>
            <Text fontSize="14px" fontWeight="700" color="solid.900" isTruncated>
              {selectedConversation ? conversationTitle(selectedConversation, currentUserId) : 'HR Chat'}
            </Text>
            <Text fontSize="11px" color="solid.400">{unreadTotal} unread</Text>
          </Box>
        </HStack>
        <HStack spacing="2px">
          <IconButton aria-label="Open HR chat page" icon={<ExternalLink size={15} />} variant="ghost" size="sm" onClick={openFullPage} />
          <IconButton aria-label="Minimize HR chat" icon={<Minus size={16} />} variant="ghost" size="sm" onClick={() => setIsOpen(false)} />
          <IconButton aria-label="Close HR chat" icon={<X size={16} />} variant="ghost" size="sm" onClick={() => setIsOpen(false)} />
        </HStack>
      </Flex>

      {visibleConversations.length > 1 && (
        <HStack spacing="8px" px="12px" py="9px" overflowX="auto" borderBottom="1px solid" borderColor="rgba(0,0,0,0.06)">
          {visibleConversations.map((conversation) => (
            <Button
              key={conversation.id}
              size="sm"
              flexShrink={0}
              borderRadius="999px"
              variant="outline"
              bg={conversation.id === selectedConversation?.id ? 'rgba(93,94,203,0.08)' : 'white'}
              borderColor={conversation.id === selectedConversation?.id ? 'rgba(93,94,203,0.22)' : 'rgba(0,0,0,0.06)'}
              color={conversation.id === selectedConversation?.id ? 'primary.100' : 'solid.700'}
              onClick={() => setSelectedConversationId(conversation.id)}
            >
              {conversationTitle(conversation, currentUserId)}
            </Button>
          ))}
        </HStack>
      )}

      <Box ref={scrollRef} flex="1" minH={0} overflowY="auto" px="14px" py="14px" bg="linear-gradient(180deg, rgba(255,255,255,0.72) 0%, rgba(248,248,252,0.92) 100%)">
        {messagesQuery.isLoading && messages.length === 0 ? (
          <Flex h="100%" align="center" justify="center"><Spinner size="sm" color="primary.100" /></Flex>
        ) : (
          <VStack align="stretch" spacing="12px">
            {messages.map((message) => {
              const isMine = message.sender_id === currentUserId
              if (message.message_type === 'system') {
                return <Text key={message.id} alignSelf="center" fontSize="11px" color="solid.400" bg="white" borderRadius="999px" px="10px" py="4px">{message.body}</Text>
              }
              return (
                <Flex key={message.id} justify={isMine ? 'flex-end' : 'flex-start'}>
                  <Box maxW="78%" bg={isMine ? 'primary.100' : 'white'} color={isMine ? 'white' : 'solid.800'} borderRadius={isMine ? '18px 18px 4px 18px' : '4px 18px 18px 18px'} px="13px" py="9px" boxShadow="0 2px 8px rgba(0,0,0,0.05)" whiteSpace="pre-wrap" wordBreak="break-word" fontSize="13px" lineHeight="1.5">
                    {message.body}
                  </Box>
                </Flex>
              )
            })}
          </VStack>
        )}
      </Box>

      <Box as="form" onSubmit={handleSend} px="12px" pb="12px" pt="9px" borderTop="1px solid" borderColor="rgba(0,0,0,0.06)" bg="rgba(255,255,255,0.72)">
        <HStack align="flex-end" borderRadius="22px" bg="white" border="1px solid" borderColor="rgba(0,0,0,0.08)" px="10px" py="7px" spacing="8px">
          <Textarea
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder="Type a message"
            minH="36px"
            maxH="90px"
            resize="none"
            border="none"
            bg="transparent"
            fontSize="13px"
            lineHeight="20px"
            _focus={{ border: 'none', boxShadow: 'none' }}
            _hover={{ border: 'none' }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault()
                sendDraft()
              }
            }}
          />
          <IconButton
            aria-label="Send HR chat message"
            icon={<Send size={16} />}
            type="submit"
            variant="unstyled"
            display="flex"
            alignItems="center"
            justifyContent="center"
            borderRadius="12px"
            bg={draft.trim() ? 'primary.100' : 'solid.100'}
            color={draft.trim() ? 'white' : 'solid.400'}
            h="32px"
            minW="32px"
            w="32px"
            isDisabled={!draft.trim() || !selectedConversation}
            isLoading={sendMessage.isPending}
          />
        </HStack>
      </Box>
    </Flex>
  )
}

export default HRChatWidget
